<script lang="ts" setup>
import { computed } from 'vue';
import { formatTimeAgo } from '@vueuse/core';
import type { Project } from '~/utilities/pyscript-api-models';
import { toProjectPage } from '~/utilities/to-project-page';
import { useUserStore } from '~/stores/user-store';
import { useProjectSettingsModalStore } from '~/stores/project-settings-modal-store';
import ProjectCatalogListColumns from '~/components/project-catalog/ProjectCatalogListColumns.vue';
import ProjectCatalogTags from '~/components/project-catalog/ProjectCatalogTags.vue';
import FavoriteButton from '~/components/buttons/FavoriteButton.vue';
import ProjectThreeDotMenu from '~/components/ProjectThreeDotMenu.vue';
import IconLock from '~icons/material-symbols/lock';

const props = defineProps<{
  project: Project;
  tagClickHandler: (...args: any) => void;
  lastItem: boolean;
}>();

defineEmits<{
  (e: 'projectDeleted', value: Project): void;
  (e: 'projectUpdated', value: Project): void;
}>();

const userStore = useUserStore();
const projectSettingsModalStore = useProjectSettingsModalStore();

const isProjectOwner = computed(() => userStore.isProjectOwner(props.project));

const lastEdited = computed(() => {
  return formatTimeAgo(new Date(props.project.updated_at));
});

function openUserProfileInNewTab(href: string) {
  if (!href) return;
  window.open(href, '_blank');
}
</script>

<template>
  <ProjectCatalogListColumns
    class="group relative block px-2 text-13 hover:bg-new-gray-50 hover:dark:bg-new-gray-900"
    :class="[lastItem ? 'rounded-b' : 'border-b border-new-gray-50 dark:border-new-gray-950/60']"
  >
    <template #favorite>
      <FavoriteButton class="relative z-1 -translate-x-[12px]" :project="project" size="md" />
    </template>

    <template #icon>
      <img
        class="mx-auto h-6 w-6 rounded bg-new-slate-300 object-cover dark:bg-new-gray-800"
        :src="`${project.icon}?size=small`"
        :alt="project.name"
        width="24"
        height="24"
        loading="lazy"
      />
    </template>

    <template #title>
      <div class="flex items-center gap-2">
        <router-link
          :to="toProjectPage(project)"
          :aria-label="project.name"
          class="truncate font-medium leading-tight before:absolute before:inset-0 before:block"
        >
          {{ project.name }}
        </router-link>

        <IconLock
          v-if="project.auth_required"
          v-tooltip="{
            placement: 'bottom',
            content: 'Private Project',
          }"
          class="shrink-0 text-xs text-new-blue-400"
        />
      </div>

      <button
        v-if="!isProjectOwner"
        class="truncate text-xs text-black/60 hover:underline dark:text-white/60"
        @mouseup.middle="
          openUserProfileInNewTab(
            $router.resolve({
              name: 'profile',
              params: { usernameOrUserId: project.username || project.user_id },
            }).href,
          )
        "
        @click="
          $router.push({
            name: 'profile',
            params: { usernameOrUserId: project.username || project.user_id },
          })
        "
      >
        @{{ project.username || project.user_id }}
      </button>

      <div class="truncate text-xs text-black/60 dark:text-white/60 sm:hidden sm:text-13">
        {{ lastEdited }}
      </div>
    </template>

    <template #description>
      <div class="text-black/60 dark:text-white/60">
        <button
          v-if="!project.description && isProjectOwner"
          class="relative italic hover:underline"
          @click="projectSettingsModalStore.open(props.project, 0)"
        >
          Add a description...
        </button>
        <p v-else class="truncate">{{ project.description }}</p>
      </div>
    </template>

    <template #tag>
      <ProjectCatalogTags :project="project" :click-handler="tagClickHandler" />
    </template>

    <template #modified>
      <p class="truncate px-1 text-right">
        <span class="leading-tight text-black/60 dark:text-white/60">{{ lastEdited }}</span>
      </p>
    </template>

    <template #menu>
      <ProjectThreeDotMenu
        :project="project"
        :can-delete="isProjectOwner"
        version="latest"
        menu-btn-class-list="rounded-full text-new-gray-800 transition-colors hover:bg-new-gray-100/50 dark:text-new-gray-100 p-1"
        @project-deleted="$emit('projectDeleted', $event)"
        @project-updated="$emit('projectUpdated', $event)"
      />
    </template>
  </ProjectCatalogListColumns>
</template>
